import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Margin } from 'styled-components-spacing';
import { Checkbox, Field, Radio, Reveal } from 'components';
import { CheckboxColumn, EmptyNote, RadioRow } from '../Reports.styled';

/**
 * Whether the Young Crew took part in any of the global events, and in which.
 *
 * The list is only asked for once the answer above it is yes: a "no" carries no
 * events, and ticking one after answering no would contradict the answer.
 */
const GlobalEvents = ({
  participated,
  selected,
  options,
  onRecord,
  errors,
  disabled,
  onParticipated,
  onToggle,
}) => {
  const { t } = useTranslation();

  const toggle = (value) => {
    onToggle(
      selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value],
    );
  };

  return (
    <>
      <Margin bottom="24">
        <Field
          id="participatedInGlobalEvents"
          label={t('page.yc.reports.form.globalEventsQuestion')}
          required
          error={errors.participatedInGlobalEvents}
        >
          <RadioRow>
            <Radio
              disabled={disabled}
              selected={participated === true}
              onClick={() => onParticipated(true)}
            >
              {t('global.yes')}
            </Radio>
            <Radio
              disabled={disabled}
              selected={participated === false}
              onClick={() => onParticipated(false)}
            >
              {t('global.no')}
            </Radio>
          </RadioRow>
        </Field>
      </Margin>

      {participated === true ? (
        <Reveal>
          <Margin bottom="0">
            <Field
              id="globalEvents"
              label={t('page.yc.reports.form.globalEventsList')}
              required
              error={errors.globalEvents}
            >
              <CheckboxColumn>
                {options.map((option) => (
                  <Checkbox
                    key={option.value}
                    disabled={disabled}
                    selected={selected.includes(option.value)}
                    onChange={() => toggle(option.value)}
                  >
                    {option.label}
                  </Checkbox>
                ))}
              </CheckboxColumn>
            </Field>
          </Margin>
        </Reveal>
      ) : null}

      {/* What the YC Management Board has on file for this year. It is their
          own record, kept in the green profile, so it is shown rather than
          edited - and it is shown even when the two answers disagree, because
          a disagreement is exactly what somebody needs to see. */}
      {onRecord.length ? (
        <Margin top="24">
          <Field id="globalEventsOnRecord" label={t('page.yc.reports.form.globalEventsOnRecord')}>
            <EmptyNote>
              {t('page.yc.reports.form.globalEventsOnRecordValue', {
                events: onRecord.map((item) => item.label).join(', '),
              })}
            </EmptyNote>
          </Field>
        </Margin>
      ) : null}
    </>
  );
};

GlobalEvents.propTypes = {
  participated: PropTypes.bool,
  selected: PropTypes.arrayOf(PropTypes.string),
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })),
  onRecord: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string }),
  ),
  errors: PropTypes.objectOf(PropTypes.string),
  disabled: PropTypes.bool,
  onParticipated: PropTypes.func,
  onToggle: PropTypes.func,
};

GlobalEvents.defaultProps = {
  participated: null,
  selected: [],
  options: [],
  onRecord: [],
  errors: {},
  disabled: false,
  onParticipated: () => undefined,
  onToggle: () => undefined,
};

export default GlobalEvents;
